import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { StatusCode } from "api/protocols";
import {
  GetTeeSheetPrint,
  ITeeSheet,
  PostEmailTeeSheet,
  PutTeeSheet,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { PostFacilityStatus, TPostFacilityStatus } from "api/rpc/2024-04/facilityAdmin/facility/status";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { formatDate, isEmpty } from "helpers/Helpers";

import Weather from "components/weather/Weather";
import { formatMarkdownBreaks } from "components/markdown/Markdown";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import InformationItem from "elements/teesheet/statisticsbar/InformationItem";
import { faBan, faCircle, faDotCircle, faPercent } from "@fortawesome/pro-regular-svg-icons";
import EmailTeesheetModal, { IEmailTeeSheet, TRecipient } from "elements/teesheet/statisticsbar/EmailTeesheetModal";
import PrintTeesheetModal, { IPrintTeesheet } from "elements/teesheet/statisticsbar/PrintTeesheetModal";
import StatusModal, { IFacilityStatusState } from "elements/teesheet/statisticsbar/StatusModal";

import { ITeeTimeNew } from "redux/reducers/models/teetime";

import "elements/teesheet/statisticsbar/statisticsBar.scss";
import OverrideCartsAvailableModal, {
  IOverrideCartsAvailableModalState,
} from "elements/teesheet/statisticsbar/OverrideCartsAvailableModal";
import TeeSheetSideBySideSettingsModal from "components/teeSheetSideBySideSettingsModal/TeeSheetSideBySideSettingsModal";
import {
  ITeeSheetSideBySideSettings,
  IUpdateTeeSheetSideBySideSettings,
} from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import TeeSheetAuditTrailModal from "components/teeSheetAuditTrailModal/teeSheetAuditTrailModal";

interface INewStatisticsBarProps {
  teeSheet: ITeeSheet;
  teeTimes: ITeeTimeNew[];
  reloadTeeTimesAndSheet: () => void;
  sideBySideSettings: ITeeSheetSideBySideSettings;
  updateSideBySideSettings: IUpdateTeeSheetSideBySideSettings;
  sideBySideSettingsKeyPrefix: string;
  teeSheetLoaded?: boolean;
}

export default function NewStatisticsBar(props: INewStatisticsBarProps) {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const { facilityStore, teeSheetStore } = useAppSelector(store => store);

  const {
    state: emailTeesheetModal,
    updateModal: updateEmailTeesheetModal,
    closeModal: closeEmailTeesheetModal,
  } = useModal<IEmailTeeSheet>({
    subject: "",
    content: "",
    recipientIds: [],
    newEmail: "",
    additionalEmails: [],
  });

  const {
    state: printTeesheetModal,
    updateModal: updatePrintTeesheetModal,
    closeModal: closePrintTeesheetModal,
  } = useModal<IPrintTeesheet>({
    startTime: "06:00",
    endTime: "21:00",
    date: null,
    fontSize: 8,
    selectedDivisionIds: [],
    selectedCourseIds: [],
    showCustomerType: false,
    showGuest: false,
    showBagStorage: false,
  });

  const {
    state: statusModal,
    updateModal: updateStatusModal,
    closeModal: closeStatusModal,
  } = useModal<IFacilityStatusState>({
    date: null,
    inputData: [] as Array<{ status_input_id: number; value: string }>,
  });

  const {
    state: overrideCartsAvailableModal,
    updateModal: updateOverrideCartsAvailableModal,
    closeModal: closeOverrideCartsAvailableModal,
  } = useModal<IOverrideCartsAvailableModalState>({
    cartsAvailableInput: "",
  });

  const {
    state: selectSideBySideSettingsModal,
    updateModal: updateSideBySideSettingsModal,
    closeModal: closeSideBySideSettingsModal,
  } = useModal();

  const {
    state: auditTeeSheetModal,
    updateModal: updateAuditTeeSheetModal,
    closeModal: closeAuditTeeSheetModal,
  } = useModal({
    isOpen: false,
    date: null,
    teeSheetId: props.teeSheet?.id,
  });

  const cartFleetStatisticActive = props.teeSheet && facilityStore.teeSheetSettings?.validate_power_cart_count;

  const cartFleetSize = useMemo(() => {
    if (cartFleetStatisticActive) {
      if (props.teeSheet.carts_available != null) {
        return props.teeSheet.carts_available;
      }

      let updatedCartFleetSize = 0;

      if (facilityStore.teeSheetSettings?.use_cart_inventory) {
        updatedCartFleetSize = props.teeSheet.inventory_carts_available ?? props.teeSheet.default_carts_available ?? 0;
      } else {
        updatedCartFleetSize = props.teeSheet.default_carts_available ?? 0;
      }

      return updatedCartFleetSize;
    } else {
      return 0;
    }
  }, [
    props.teeSheet?.carts_available,
    props.teeSheet?.default_carts_available,
    props.teeSheet?.inventory_carts_available,
    facilityStore.teeSheetSettings?.use_cart_inventory,
  ]);

  const recipients: TRecipient[] = useMemo(() => {
    const updatedRecipients: TRecipient[] = [];

    if (!props.teeTimes) {
      return updatedRecipients;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type !== "crossover" && teeTime.slots[0].quantity_remaining !== 4) {
        updatedRecipients.push({
          id: teeTime.slots[0].tee_time_id,
          start_time: teeTime.start_time,
          division_id: teeTime.slots[0].division_id,
          course_id: teeTime.slots[0].course_id,
        });
      }
    });

    return updatedRecipients;
  }, [props.teeTimes]);

  const bookedSlotsCount = useMemo(() => {
    let count = 0;

    if (!props.teeTimes) {
      return count;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status === "booked") {
            count += 1;
          }
        });
      }
    });

    return count;
  }, [props.teeTimes]);

  const availableSlotsCount = useMemo(() => {
    let count = 0;

    if (!props.teeTimes) {
      return count;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status === "available") {
            count += 1;
          }
        });
      }
    });

    return count;
  }, [props.teeTimes]);

  const currentNoShows = useMemo(() => {
    const count: string[] = [];

    if (!props.teeTimes) {
      return {
        total: 0,
        no_show_charge: 0,
        no_show_weather: 0,
        no_show_defer: 0,
      };
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.customer_id && slot.check_in_status === "no_show") {
            count.push(slot.no_show_type);
          }
        });
      }
    });

    // Add more filters to return obj for more no_shows
    return {
      total: count.length,
      no_show_charge: count.filter(val => val === "charge").length,
      no_show_weather: count.filter(val => val === "weather").length,
      no_show_defer: count.filter(val => val === "defer").length,
    };
  }, [props.teeTimes]);

  async function sendTeesheetEmail() {
    const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

    const params = {
      date: formattedDate,
      subject: emailTeesheetModal.subject,
      content: formatMarkdownBreaks(emailTeesheetModal.content),
      tee_time_ids: emailTeesheetModal.recipientIds,
      additional_email_addresses: emailTeesheetModal.additionalEmails,
    };

    const res = await PostEmailTeeSheet(params, true);

    if (res.status !== StatusCode.OK) {
      return;
    }

    closeEmailTeesheetModal();
  }

  async function printTeesheet() {
    const formattedDate = formatDate(printTeesheetModal.date);

    const params = {
      date: formattedDate,
      start_time: printTeesheetModal.startTime,
      end_time: printTeesheetModal.endTime,
      division_ids: printTeesheetModal.selectedDivisionIds,
      course_ids: printTeesheetModal.selectedCourseIds,
      font_size: printTeesheetModal.fontSize,
      show_bag_storage: printTeesheetModal.showBagStorage,
      show_customer_type: printTeesheetModal.showCustomerType,
    };

    const teeSheetRes = await GetTeeSheetPrint(params, true);

    if (teeSheetRes.status === StatusCode.OK) {
      window.open().document.write(String(teeSheetRes.data));
    } else {
      dispatch(showError(t("elements.tee_sheet.statistic_bar.013")));
    }
  }

  async function saveFacilityStatus() {
    const { ...rest } = statusModal;
    const params: TPostFacilityStatus = {
      date: formatDate(rest.date),
      input_data: rest.inputData,
    };

    const res = await PostFacilityStatus(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.statistic_bar.029")));
      return;
    }

    dispatch(showSuccess(t("elements.tee_sheet.statistic_bar.030")));
    closeStatusModal();
  }

  async function saveOverrideCartsAvailable() {
    if (!cartFleetStatisticActive) {
      return;
    }

    const updatedCartsAvailable = isEmpty(overrideCartsAvailableModal.cartsAvailableInput)
      ? null
      : Number(overrideCartsAvailableModal.cartsAvailableInput);

    const putTeeSheetRes = await PutTeeSheet(
      {
        id: props.teeSheet?.id,
        carts_available: updatedCartsAvailable,
      },
      true,
    );

    if (putTeeSheetRes.status !== StatusCode.OK) {
      return;
    }

    props.reloadTeeTimesAndSheet();

    updateOverrideCartsAvailableModal({ isOpen: false });
  }

  function openOverrideCartsAvailableModal() {
    if (cartFleetStatisticActive) {
      updateOverrideCartsAvailableModal({ isOpen: true });
    }
  }

  function saveSideBySideSettings(updatedSideBySideSettings: ITeeSheetSideBySideSettings) {
    props.updateSideBySideSettings(props.sideBySideSettingsKeyPrefix, updatedSideBySideSettings);
    updateSideBySideSettingsModal({ isOpen: false });
  }

  return (
    <div className="statistics-bar">
      <div className="statistics-bar-booking-content">
        <InformationItem
          label={t("elements.tee_sheet.statistic_bar.001")}
          icon={faDotCircle}
          value={bookedSlotsCount}
        />
        <InformationItem
          label={t("elements.tee_sheet.statistic_bar.002")}
          icon={faCircle}
          value={availableSlotsCount}
        />
        <InformationItem
          label={t("elements.tee_sheet.statistic_bar.003")}
          icon={faPercent}
          value={`${Math.round((bookedSlotsCount / (bookedSlotsCount + availableSlotsCount)) * 100)}%`}
        />
        <InformationItem
          label={
            currentNoShows.total === 1
              ? t("elements.tee_sheet.statistic_bar.031")
              : t("elements.tee_sheet.statistic_bar.032")
          }
          icon={faBan}
          value={currentNoShows.total}
          hoverable
        >
          <ul className="information-item-hover-content">
            <li>
              <p className="type">{t("elements.tee_sheet.statistic_bar.033")}</p>
              <p className="value">{currentNoShows.no_show_charge}</p>
            </li>
            <li>
              <p className="type">{t("elements.tee_sheet.statistic_bar.034")}</p>
              <p className="value">{currentNoShows.no_show_defer}</p>
            </li>
            <li>
              <p className="type">{t("elements.tee_sheet.statistic_bar.035")}</p>
              <p className="value">{currentNoShows.no_show_weather}</p>
            </li>
          </ul>
        </InformationItem>
        {cartFleetStatisticActive && (
          <InformationItem
            customIcon="../../../public/images/powercart2-01.svg"
            value={cartFleetSize}
            label={cartFleetSize === 1 ? t("Cart") : t("Carts")} // TODO: Translation
            onClick={openOverrideCartsAvailableModal}
          />
        )}
      </div>
      <div className="statistics-bar-action-content">
        <div>{facilityStore.weather && <Weather weather={facilityStore.weather} />}</div>

        <div className="statistics-bar-action-content-actions">
          <NavigationDropdownNew
            rightAlign
            label={t("elements.tee_sheet.statistic_bar.036")}
            type="text"
            sections={[
              [
                {
                  label: t("elements.tee_sheet.statistic_bar.004"),
                  onClick: () => updatePrintTeesheetModal({ isOpen: true, date: teeSheetStore.selectedDate as Date }),
                  icon: "print",
                },
              ],
              [
                {
                  label: t("elements.tee_sheet.statistic_bar.037"),
                  onClick: () => updateStatusModal({ isOpen: true, date: teeSheetStore.selectedDate as Date }),
                  icon: "calendars", // TEMP ICON
                  hidden: false,
                },
              ],
              [
                {
                  label: t("elements.tee_sheet.statistic_bar.005"),
                  onClick: () => updateEmailTeesheetModal({ isOpen: true }),
                  icon: "envelope",
                  hidden: false,
                },
              ],
              [
                {
                  label: t("elements.tee_sheet.statistic_bar.006"),
                  onClick: () => history.push("/admin/teesheet/edit"),
                  icon: "edit",
                  hidden: false,
                },
              ],
              [
                {
                  label: t("Side by side view"), // TODO: Translation
                  onClick: () => updateSideBySideSettingsModal({ isOpen: true }),
                  icon: "table-columns",
                  disabled: props.sideBySideSettingsKeyPrefix == null,
                  hidden: false,
                },
              ],
              [
                {
                  label: "Audit Tee Sheet", // TODO: Translation
                  onClick: () => updateAuditTeeSheetModal({ isOpen: true, date: teeSheetStore.selectedDate as Date }),
                  icon: "history",
                  hidden: false,
                  //disabled: !props.teeSheetLoaded,
                },
              ],
            ]}
          />
        </div>
      </div>

      <EmailTeesheetModal
        state={emailTeesheetModal}
        update={updateEmailTeesheetModal}
        close={closeEmailTeesheetModal}
        recipients={recipients}
        onOk={() => void sendTeesheetEmail()}
        courses={facilityStore.facility?.courses}
      />

      <PrintTeesheetModal
        state={printTeesheetModal}
        update={updatePrintTeesheetModal}
        close={closePrintTeesheetModal}
        onOk={() => void printTeesheet()}
        facility={facilityStore.facility}
      />

      <StatusModal
        state={statusModal}
        update={updateStatusModal}
        close={closeStatusModal}
        onOk={() => void saveFacilityStatus()}
      />

      <OverrideCartsAvailableModal
        cartsAvailable={props.teeSheet?.carts_available}
        state={overrideCartsAvailableModal}
        update={updateOverrideCartsAvailableModal}
        close={closeOverrideCartsAvailableModal}
        onOk={() => void saveOverrideCartsAvailable}
      />

      <TeeSheetSideBySideSettingsModal
        open={selectSideBySideSettingsModal.isOpen}
        onCancel={closeSideBySideSettingsModal}
        onOk={settings => void saveSideBySideSettings(settings)}
        currentSideBySideSettings={props.sideBySideSettings}
        courses={facilityStore.facility?.courses}
      />

      <TeeSheetAuditTrailModal
        state={auditTeeSheetModal}
        update={updateAuditTeeSheetModal}
        close={closeAuditTeeSheetModal}
        onOk={() => void closeAuditTeeSheetModal()}
      />
    </div>
  );
}
